import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { db, storage } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { decryptData } from "../utils/decryption";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../translations";
import Chart from "chart.js/auto";
import "../styles/sharedReport.css";

Chart.defaults.font.size = 13;

// ── Utilities ─────────────────────────────────────────────────────────────────

function formatDate(epochTime, locale = "en-US") {
  return new Date(epochTime).toLocaleString(locale, {
    month: "2-digit", day: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function formatDuration(ms, T) {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return T.formatDuration(h, m, s);
}

function lightenColor(hex, percent = 70) {
  hex = (hex || "#888888").replace(/^#/, "");
  const r = Math.round(Math.min(255, parseInt(hex.slice(0,2),16) + (255 - parseInt(hex.slice(0,2),16)) * percent/100)).toString(16).padStart(2,"0");
  const g = Math.round(Math.min(255, parseInt(hex.slice(2,4),16) + (255 - parseInt(hex.slice(2,4),16)) * percent/100)).toString(16).padStart(2,"0");
  const b = Math.round(Math.min(255, parseInt(hex.slice(4,6),16) + (255 - parseInt(hex.slice(4,6),16)) * percent/100)).toString(16).padStart(2,"0");
  return `#${r}${g}${b}`;
}

function applyInsert(original, loc, text) {
  return original.slice(0, loc - 1) + text + original.slice(loc - 1);
}

function applyDelete(original, start, end) {
  return original.slice(0, start - 1) + original.slice(end);
}

function filterEditsByUser(edits, userId) {
  return userId === "default" ? edits : edits.filter((e) => e.userId === userId);
}

function filteredEditsByTab(edits, tab) {
  return edits.filter((e) => e.tab === tab);
}

function getTabsList(edits) {
  const tabs = [];
  edits.forEach((e) => { if (!tabs.includes(e.tab)) tabs.push(e.tab); });
  return tabs;
}

function calcWritingSessions(edits) {
  if (!edits || edits.length === 0) return { totalTime: 0, sessions: [] };
  let totalTime = 0, revisions = 0;
  const sessions = [];
  let startTime = edits[0].time;
  for (let i = 0; i < edits.length - 1; i++) {
    revisions++;
    if (edits[i + 1].time - edits[i].time > 600000 || i === edits.length - 2) {
      const duration = new Date(edits[i].time) - startTime;
      sessions.push({ startTime: new Date(startTime), endTime: new Date(edits[i].time), duration, revisions });
      totalTime += duration;
      startTime = edits[i + 1].time;
      revisions = 0;
    }
  }
  if (sessions.length === 0 && edits.length > 0) {
    const duration = new Date(edits[edits.length - 1].time) - edits[0].time;
    sessions.push({ startTime: new Date(edits[0].time), endTime: new Date(edits[edits.length - 1].time), duration, revisions: edits.length });
    totalTime += duration;
  }
  return { totalTime, sessions };
}

function calcStats(edits) {
  let content = "", deletes = 0;
  edits.forEach((e) => {
    if (e.ty === "is") content = content.slice(0, e.loc - 1) + e.text + content.slice(e.loc - 1);
    else if (e.ty === "ds") { content = content.slice(0, e.si - 1) + content.slice(e.ei); deletes++; }
  });
  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
  const totalMinutes = Math.floor(calcWritingSessions(edits).totalTime / 60000);
  return { wordCount, deletes, totalMinutes, revisions: edits.length };
}

async function calcCopied(edits, userId = "default", showLinks = true) {
  const copyNum = 50;
  const tabs = getTabsList(edits);
  const t0 = performance.now();

  let candidates = edits
    .map((item, index) => ({ ...item, index }))
    .filter((item) => item.ty === "is" && item.text.length > copyNum);

  if (userId !== "default") candidates = candidates.filter((c) => c.userId === userId);
  if (!showLinks) candidates = candidates.filter((c) => !(c.text.trim().startsWith("http") && !c.text.includes(" ")));

  const content = tabs.map(() => "");
  const unique = [];

  for (let i = 0; i < edits.length; i++) {
    if (i % 700 === 0) {
      if (performance.now() - t0 > 7000) return [];
      await new Promise((r) => setTimeout(r, 0));
    }
    const edit = edits[i];
    const ti = tabs.indexOf(edit.tab);
    if (edit.ty === "is") {
      content[ti] = applyInsert(content[ti], edit.loc, edit.text);
    } else if (edit.ty === "ds") {
      const deleted = content[ti].slice(edit.si - 1, edit.ei);
      content[ti] = applyDelete(content[ti], edit.si, edit.ei);
      if (deleted.length >= copyNum) {
        for (let j = 0; j < candidates.length; j++) {
          if (candidates[j].text === deleted || candidates.index > edit.index) candidates.splice(j--, 1);
        }
      }
    }
    if (candidates.length > 0 && (i === candidates[0]?.index - 1 || candidates[0].index === 0)) {
      if (!content.join("").includes(candidates[0].text) || candidates[0].index === 0) unique.push(candidates[0]);
      candidates.shift();
    }
  }
  return unique;
}

function replayEditsCalc(edits, index, originalDoc, curContent = null, curIndex = -1) {
  let content, prevContent;
  if (curContent !== null && curIndex >= 0 && index === curIndex + 1 && index < edits.length) {
    prevContent = curContent;
    content = curContent;
    const e = edits[index];
    if (e.ty === "is") content = applyInsert(content, e.loc, e.text);
    else if (e.ty === "ds") content = applyDelete(content, e.si, e.ei);
  } else if (curContent !== null && curIndex >= 0 && index > curIndex) {
    prevContent = curContent; content = curContent;
    for (let i = curIndex + 1; i < index; i++) {
      const e = edits[i];
      if (e.ty === "is") { prevContent = applyInsert(prevContent, e.loc, e.text); content = applyInsert(content, e.loc, e.text); }
      else if (e.ty === "ds") { prevContent = applyDelete(prevContent, e.si, e.ei); content = applyDelete(content, e.si, e.ei); }
    }
    if (index < edits.length) {
      const e = edits[index];
      if (e.ty === "is") content = applyInsert(content, e.loc, e.text);
      else if (e.ty === "ds") content = applyDelete(content, e.si, e.ei);
    }
  } else {
    content = originalDoc; prevContent = originalDoc;
    for (let i = 0; i < index; i++) {
      const e = edits[i];
      if (e.ty === "is") prevContent = applyInsert(prevContent, e.loc, e.text);
      else if (e.ty === "ds") prevContent = applyDelete(prevContent, e.si, e.ei);
    }
    for (let i = 0; i <= index; i++) {
      const e = edits[i];
      if (e.ty === "is") content = applyInsert(content, e.loc, e.text);
      else if (e.ty === "ds") content = applyDelete(content, e.si, e.ei);
    }
  }
  return { content, prevContent, index };
}

function buildPlaybackHTML(content, edit, prevContent) {
  if (!edit) return content;
  if (edit.ty === "is") {
    return content.slice(0, edit.loc - 1) + `<ins id="scrollSpan" class="insert">${edit.text}</ins>` + content.slice(edit.loc + edit.text.length - 1);
  }
  if (edit.ty === "ds") {
    return prevContent.slice(0, edit.si - 1) + `<del id="scrollSpan" class="delete">${prevContent.slice(edit.si - 1, edit.ei)}</del>` + prevContent.slice(edit.ei);
  }
  return content;
}

function buildGroupBreakdownHTML(edits, originalDoc, sliderValue, activeTab, users) {
  const tabEdits = filteredEditsByTab(edits, activeTab).slice(0, sliderValue + 1);
  let content = originalDoc;
  let ownership = Array(originalDoc.length).fill(null);
  for (const e of tabEdits) {
    if (e.ty === "is") {
      ownership = [...ownership.slice(0, e.loc - 1), ...Array(e.text.length).fill(e.userId), ...ownership.slice(e.loc - 1)];
      content = applyInsert(content, e.loc, e.text);
    } else if (e.ty === "ds") {
      ownership = [...ownership.slice(0, e.si - 1), ...ownership.slice(e.ei)];
      content = applyDelete(content, e.si, e.ei);
    }
  }
  let html = "", cur = ownership[0], buf = content[0] || "";
  for (let i = 1; i < content.length; i++) {
    if (ownership[i] === cur) { buf += content[i]; }
    else {
      html += cur && users[cur] ? `<span style="background-color:${lightenColor(users[cur].color)}">${buf}</span>` : buf;
      buf = content[i]; cur = ownership[i];
    }
  }
  html += buf ? (cur && users[cur] ? `<span style="background-color:${lightenColor(users[cur].color)}">${buf}</span>` : buf) : "";
  return html;
}

function buildDateChartData(edits) {
  const xValues = [], yValues = [];
  let count = 0, curTime = new Date(edits[0].time);
  edits.forEach((e, i) => {
    const d = new Date(e.time);
    if (d.toDateString() === curTime.toDateString()) { count++; }
    else { xValues.push(`${curTime.getFullYear()}/${curTime.getMonth()+1}/${curTime.getDate()}`); yValues.push(count); curTime = d; count = 1; }
    if (i === edits.length - 1) { xValues.push(`${curTime.getFullYear()}/${curTime.getMonth()+1}/${curTime.getDate()}`); yValues.push(count); }
  });
  return { xValues, yValues };
}

function buildTimeChartData(edits, date, T) {
  let filtered = edits, dateLabel = T.allDates;
  if (date && date !== "all dates") {
    filtered = edits.filter((e) => { const d = new Date(e.time); return d.toDateString() === date.toDateString(); });
    dateLabel = `${date.getFullYear()}/${String(date.getMonth()+1).padStart(2,"0")}/${String(date.getDate()).padStart(2,"0")}`;
  }
  const hours = filtered.map((e) => new Date(e.time).getHours()).sort((a,b) => a-b);
  const unique = [...new Set(hours)];
  const xValues = unique.map((hr) => T.hourLabel(hr));
  const yValues = unique.map((hr) => hours.filter((h) => h===hr).length);
  return { xValues, yValues, dateLabel };
}

function buildTimePerDayData(edits) {
  const xValues = [], yValues = [];
  let curTime = new Date(edits[0].time), editsPerDay = [], editsOnDay = [];
  edits.forEach((e, i) => {
    const d = new Date(e.time);
    if (d.toDateString() === curTime.toDateString()) { editsOnDay.push(e); }
    else { editsPerDay.push(editsOnDay); editsOnDay = [e]; curTime = d; }
    if (i === edits.length - 1 && editsOnDay.length) editsPerDay.push(editsOnDay);
  });
  editsPerDay.forEach((day) => {
    if (!day.length) return;
    const d = new Date(day[0].time);
    xValues.push(`${d.getFullYear()}/${d.getMonth()+1}/${d.getDate()}`);
    yValues.push(Math.round(calcWritingSessions(day).totalTime / 60000));
  });
  return { xValues, yValues };
}

const BAR_COLOR = "rgba(61,212,212,1)";
const BAR_BORDER = "rgba(61,212,212,0.1)";

// ── Component ─────────────────────────────────────────────────────────────────

const SharedReport = () => {
  const { shareId } = useParams();
  const { lang } = useLanguage();
  const T = translations[lang].sharedReport;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  const [activeTab, setActiveTab] = useState("first");
  const [selectedReportUser, setSelectedReportUser] = useState("default");
  const [sliderValue, setSliderValue] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);
  const [playbackHTML, setPlaybackHTML] = useState("");
  const [playbackDate, setPlaybackDate] = useState("");
  const [groupGraphMode, setGroupGraphMode] = useState("time");
  const [copiedTexts, setCopiedTexts] = useState([]);
  const [showAllCopied, setShowAllCopied] = useState(false);
  const [showLinks, setShowLinks] = useState(true);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [selectedGraphDate, setSelectedGraphDate] = useState(null);
  const [graphDateLabel, setGraphDateLabel] = useState(T.allDates);
  const [showGroupBreakdown, setShowGroupBreakdown] = useState(false);
  const [groupBreakdownHTML, setGroupBreakdownHTML] = useState("");

  // Mutable refs (not triggering re-renders)
  const playIntervalRef = useRef(null);
  const currentStateRef = useRef({ content: null, index: -1 });
  const currentIndexRef = useRef(0);
  const playbackDivRef = useRef(null);

  // Stable refs for values used inside setInterval closures
  const editsRef = useRef([]);
  const activeTabRef = useRef("first");
  const originalDocRef = useRef("");
  const speedRef = useRef(2);
  const sliderRef = useRef(0);
  const localeRef = useRef(T.dateLocale);

  // Chart canvas + instance refs
  const dateCanvasRef = useRef(null);
  const timeCanvasRef = useRef(null);
  const timeDayCanvasRef = useRef(null);
  const groupCanvasRef = useRef(null);
  const dateChart = useRef(null);
  const timeChart = useRef(null);
  const timeDayChart = useRef(null);
  const groupChart = useRef(null);

  const edits = reportData?.edits || [];
  const users = reportData?.userMap || {};
  const tabsData = reportData?.tabsData || {};
  const originalDoc = reportData?.originalDoc || "";

  // Keep refs in sync
  editsRef.current = edits;
  activeTabRef.current = activeTab;
  originalDocRef.current = originalDoc;
  speedRef.current = speed;
  sliderRef.current = sliderValue;
  localeRef.current = T.dateLocale;

  const tabs = useMemo(() => getTabsList(edits), [edits]);
  const filteredEdits = useMemo(() => filteredEditsByTab(edits, activeTab), [edits, activeTab]);
  const reportEdits = useMemo(() => filterEditsByUser(edits, selectedReportUser), [edits, selectedReportUser]);
  const stats = useMemo(() => (reportEdits.length ? calcStats(reportEdits) : null), [reportEdits]);
  const { sessions: writingSessions } = useMemo(
    () => (reportEdits.length ? calcWritingSessions(reportEdits) : { totalTime: 0, sessions: [] }),
    [reportEdits]
  );

  const reportUserLabel = selectedReportUser === "default" ? T.allUsers : (users[selectedReportUser]?.name || T.unknown);
  const displayedCopied = showAllCopied ? copiedTexts : copiedTexts.slice(0, 3);
  const displayedSessions = showAllSessions ? writingSessions : writingSessions.slice(0, 3);

  // ── Fetch & decrypt ──────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        if (!shareId) throw new Error();
        const key = window.location.hash.substring(1);
        if (!key) throw new Error();
        const snap = await getDoc(doc(db, "sharedReports", shareId));
        if (!snap.exists()) throw new Error();
        const data = snap.data();
        if (data.expiresAt && data.expiresAt.toDate() < new Date()) throw new Error();
        const { storagePath, salt, iv } = data;
        if (!storagePath || !salt || !iv) throw new Error();
        const downloadURL = await getDownloadURL(ref(storage, storagePath));
        const response = await fetch(downloadURL);
        if (!response.ok) throw new Error();
        const encryptedData = await response.text();
        setReportData(await decryptData(encryptedData, salt, iv, key));
      } catch {
        setError(T.errors.notFound);
      } finally {
        setLoading(false);
      }
    };
    if (shareId) load(); else { setError(T.errors.notFound); setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId]);

  // ── Reset playback when data loads or tab switches ──────────────────────────
  useEffect(() => {
    if (!reportData) return;
    currentStateRef.current = { content: null, index: -1 };
    currentIndexRef.current = 0;
    sliderRef.current = 0;
    setSliderValue(0);
    setPlaybackHTML(originalDoc);
    setPlaybackDate("");
    setShowGroupBreakdown(false);
  }, [reportData, activeTab]);

  // ── Copied texts (async) ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!edits.length) return;
    setShowAllCopied(false);
    calcCopied(edits, selectedReportUser, showLinks).then(setCopiedTexts);
  }, [selectedReportUser, showLinks, reportData]);

  // ── Scroll playback to highlighted span ─────────────────────────────────────
  useEffect(() => {
    const div = playbackDivRef.current;
    if (!div) return;
    const el = div.querySelector("#scrollSpan");
    if (!el) return;
    const cr = div.getBoundingClientRect(), er = el.getBoundingClientRect();
    if (er.top < cr.top || er.bottom > cr.bottom - 10) {
      div.scrollTop = el.offsetTop - div.offsetTop;
    }
  }, [playbackHTML]);

  // ── Play interval ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isPlaying) { clearInterval(playIntervalRef.current); return; }
    playIntervalRef.current = setInterval(() => {
      const fe = filteredEditsByTab(editsRef.current, activeTabRef.current);
      const idx = currentIndexRef.current;
      if (idx < fe.length) {
        const prev = currentStateRef.current.content ?? originalDocRef.current;
        const result = replayEditsCalc(fe, idx, originalDocRef.current, currentStateRef.current.content, currentStateRef.current.index);
        currentStateRef.current = { content: result.content, index: result.index };
        currentIndexRef.current = idx + 1;
        sliderRef.current = idx;
        setSliderValue(idx);
        setPlaybackHTML(buildPlaybackHTML(result.content, fe[idx], prev));
        setPlaybackDate(formatDate(fe[idx].time, localeRef.current));
      } else {
        clearInterval(playIntervalRef.current);
        setIsPlaying(false);
      }
    }, 50 / speedRef.current);
    return () => clearInterval(playIntervalRef.current);
  }, [isPlaying, speed]);

  // ── Charts ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    const re = filterEditsByUser(reportData?.edits || [], selectedReportUser);
    if (!re.length || !dateCanvasRef.current) return;
    if (dateChart.current) dateChart.current.destroy();
    const { xValues, yValues } = buildDateChartData(re);
    dateChart.current = new Chart(dateCanvasRef.current, {
      type: "bar",
      data: { labels: xValues, datasets: [{ backgroundColor: BAR_COLOR, borderColor: BAR_BORDER, data: yValues, maxBarThickness: 35 }] },
      options: {
        plugins: { legend: { display: false } },
        scales: { x: { title: { display: true, text: T.chartDate } }, y: { title: { display: true, text: T.chartNumberOfEdits } } },
        onClick: (_, els) => { if (els?.length) setSelectedGraphDate(new Date(xValues[els[0].index])); },
      },
    });
    return () => { dateChart.current?.destroy(); dateChart.current = null; };
  }, [selectedReportUser, reportData, T]);

  useEffect(() => {
    const re = filterEditsByUser(reportData?.edits || [], selectedReportUser);
    if (!re.length || !timeCanvasRef.current) return;
    if (timeChart.current) timeChart.current.destroy();
    const { xValues, yValues, dateLabel } = buildTimeChartData(re, selectedGraphDate, T);
    setGraphDateLabel(dateLabel);
    timeChart.current = new Chart(timeCanvasRef.current, {
      type: "bar",
      data: { labels: xValues, datasets: [{ backgroundColor: BAR_COLOR, borderColor: BAR_BORDER, data: yValues, maxBarThickness: 35 }] },
      options: {
        plugins: { legend: { display: false } },
        scales: { x: { title: { display: true, text: T.chartTimeOfDay } }, y: { title: { display: true, text: T.chartNumberOfEdits } } },
      },
    });
    return () => { timeChart.current?.destroy(); timeChart.current = null; };
  }, [selectedReportUser, selectedGraphDate, reportData, T]);

  useEffect(() => {
    const re = filterEditsByUser(reportData?.edits || [], selectedReportUser);
    if (!re.length || !timeDayCanvasRef.current) return;
    if (timeDayChart.current) timeDayChart.current.destroy();
    const { xValues, yValues } = buildTimePerDayData(re);
    timeDayChart.current = new Chart(timeDayCanvasRef.current, {
      type: "bar",
      data: { labels: xValues, datasets: [{ backgroundColor: BAR_COLOR, borderColor: BAR_BORDER, data: yValues, maxBarThickness: 35 }] },
      options: {
        plugins: { legend: { display: false } },
        scales: { x: { title: { display: true, text: T.chartDate } }, y: { title: { display: true, text: T.chartTimeSpentMinutes } } },
      },
    });
    return () => { timeDayChart.current?.destroy(); timeDayChart.current = null; };
  }, [selectedReportUser, reportData, T]);

  useEffect(() => {
    const allEdits = reportData?.edits || [];
    const allUsers = reportData?.userMap || {};
    if (!allEdits.length || !groupCanvasRef.current) return;
    if (groupChart.current) groupChart.current.destroy();
    const names = [], colors = [], timeData = [], editsData = [];
    for (const key in allUsers) {
      const ue = filterEditsByUser(allEdits, key);
      names.push(allUsers[key].name || T.anonymous);
      colors.push(allUsers[key].color);
      timeData.push(Math.floor(calcWritingSessions(ue).totalTime / 60000));
      editsData.push(ue.length);
    }
    const data = groupGraphMode === "time" ? timeData : editsData;
    groupChart.current = new Chart(groupCanvasRef.current, {
      type: "pie",
      data: { labels: names, datasets: [{ data, backgroundColor: colors, borderColor: colors, borderWidth: 1 }] },
      options: { plugins: { tooltip: { callbacks: { label: (ctx) => `${ctx.parsed} ${groupGraphMode === "time" ? T.tooltipMin : T.tooltipEdits}` } } } },
    });
    return () => { groupChart.current?.destroy(); groupChart.current = null; };
  }, [groupGraphMode, reportData, T]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const applyIndex = useCallback((fe, newIndex) => {
    const result = replayEditsCalc(fe, newIndex, originalDocRef.current, currentStateRef.current.content, currentStateRef.current.index);
    currentStateRef.current = { content: result.content, index: result.index };
    currentIndexRef.current = newIndex;
    sliderRef.current = newIndex;
    setSliderValue(newIndex);
    setPlaybackHTML(buildPlaybackHTML(result.content, fe[newIndex], result.prevContent));
    setPlaybackDate(newIndex < fe.length ? formatDate(fe[newIndex].time, T.dateLocale) : "");
    setShowGroupBreakdown(false);
  }, [T]);

  const handleSliderChange = useCallback((e) => {
    if (isPlaying) setIsPlaying(false);
    const fe = filteredEditsByTab(editsRef.current, activeTabRef.current);
    applyIndex(fe, parseInt(e.target.value));
  }, [isPlaying, applyIndex]);

  const handleStep = useCallback((dir) => {
    if (isPlaying) setIsPlaying(false);
    const fe = filteredEditsByTab(editsRef.current, activeTabRef.current);
    const next = dir === "next"
      ? Math.min(sliderRef.current + 1, fe.length - 1)
      : Math.max(sliderRef.current - 1, 0);
    applyIndex(fe, next);
  }, [isPlaying, applyIndex]);

  const handleTabChange = useCallback((tab) => {
    setIsPlaying(false);
    clearInterval(playIntervalRef.current);
    setActiveTab(tab);
    currentStateRef.current = { content: null, index: -1 };
    currentIndexRef.current = 0;
  }, []);

  const handleGroupBreakdown = () => {
    if (isPlaying) setIsPlaying(false);
    setTimeout(() => {
      setGroupBreakdownHTML(buildGroupBreakdownHTML(edits, originalDoc, sliderValue, activeTab, users));
      setShowGroupBreakdown(true);
    }, 100);
  };

  const handleCopiedClick = (copied) => {
    const fe = filteredEditsByTab(edits, copied.tab);
    let fi = 0;
    for (let j = 0; j < copied.index; j++) { if (edits[j]?.tab === copied.tab) fi++; }
    const jump = () => {
      applyIndex(fe, fi);
      document.getElementById("playbackSlider")?.scrollIntoView();
    };
    if (activeTab !== copied.tab) { handleTabChange(copied.tab); setTimeout(jump, 150); }
    else jump();
  };

  const getTabLabel = (tab) => {
    for (const key in tabsData) {
      if (key.includes(tab)) {
        const t = String(tabsData[key]);
        return t.length > 23 ? t.slice(0, 23) + "..." : t;
      }
    }
    return T.deletedTab;
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) return <div className="loading">{T.loading}</div>;
  if (error) return <div className="alert alert-error">{T.errorPrefix}: {error}</div>;
  if (!reportData) return null;

  const multiUser = Object.keys(users).length > 1;

  return (
    <div className="shared-report-container">
      <h1>{reportData.documentTitle}</h1>
      <hr />

      {/* ── Replay of Edits ─────────────────────────────────────────────── */}
      <div className="ReplayEdits">
        <h1>{T.replayOfEdits}</h1>

        <div className="controls">
          <input
            type="range" id="playbackSlider" className="playback-slider"
            min={0} max={Math.max(filteredEdits.length - 1, 0)} value={sliderValue}
            onChange={handleSliderChange}
          />
          <label htmlFor="speedDropdown">{T.speed}&nbsp;</label>
          <select id="speedDropdown" value={speed} onChange={(e) => setSpeed(parseFloat(e.target.value))}>
            <option value="0.1">{T.speedSlow}</option><option value="0.25">2</option><option value="0.5">3</option>
            <option value="1">4</option><option value="2">5</option><option value="3">6</option>
            <option value="5">7</option><option value="6">8</option><option value="7">9</option>
            <option value="10">10</option><option value="12">{T.speedFast}</option>
          </select>
          <button className="prev-edit-button" onClick={() => handleStep("prev")} title={T.prevEdit}>◀</button>
          <button className="play-button" onClick={() => { setShowGroupBreakdown(false); setIsPlaying((p) => !p); }}>{isPlaying ? T.pause : T.play}</button>
          <button className="next-edit-button" onClick={() => handleStep("next")} title={T.nextEdit}>▶</button>
        </div>

        <p className="date-display">{playbackDate}</p>

        <div className="docs-container">
          <div className="tabs-sidebar">
            <div id="tabsContainer">
              <h4>{T.documentTabs}</h4>
              {tabs.map((tab) => (
                <div key={tab} className={`tab${activeTab === tab ? " active" : ""}`} onClick={() => handleTabChange(tab)}>
                  {getTabLabel(tab)}
                </div>
              ))}
            </div>
            <br /><hr /><br />
            {stats && (
              <div id="stats">
                <h4>{T.documentStatsFor} <span className="reportUser">{reportUserLabel}</span></h4>
                <p className="stat">{T.wordCount}: {stats.wordCount}</p>
                <p className="stat">{T.deletes}: {stats.deletes}</p>
                <p className="stat">
                  <span className="tooltip revisions-tooltip">
                    <p>{T.timeSpent}</p>
                    <span className="tooltiptext">{T.timeSpentTooltip}</span>
                  </span>
                  : {T.timeHrMin(Math.floor(stats.totalMinutes / 60), stats.totalMinutes % 60)}
                </p>
                <p className="stat">
                  <span className="tooltip revisions-tooltip">
                    <p>{T.editsLabel}</p>
                    <span className="tooltiptext">{T.editsTooltip}</span>
                  </span>
                  : {stats.revisions}
                </p>
              </div>
            )}
            <br /><hr /><br />
            <button className="group-breakdown-btn" onClick={handleGroupBreakdown}>{T.viewGroupBreakdown}</button>
            <div id="groupBreakDownUsers">
              {showGroupBreakdown && Object.entries(users).map(([k, u]) => (
                <p key={k} className="usernametag" style={{ backgroundColor: lightenColor(u.color) }}>
                  {u.name || T.anonymous}
                </p>
              ))}
            </div>
          </div>

          <div
            id="playback" ref={playbackDivRef} className="playback"
            dangerouslySetInnerHTML={{ __html: showGroupBreakdown ? groupBreakdownHTML : playbackHTML }}
          />
        </div>
      </div>

      <hr />

      {/* ── Writing Report ──────────────────────────────────────────────── */}
      <h1 className="report-heading">{T.writingReportFor} <span>{reportUserLabel}</span></h1>
      <br />
      <label className="text" htmlFor="userDropdownReport">{T.selectUser}&nbsp;</label>
      <select
        id="userDropdownReport" value={selectedReportUser}
        onChange={(e) => { setSelectedReportUser(e.target.value); setShowAllCopied(false); }}
      >
        <option value="default">{T.allUsers}</option>
        {Object.entries(users).map(([k, v]) => <option key={k} value={k}>{v.name || T.anonymousUsers}</option>)}
      </select>
      <br />

      {/* ── Copy/Paste ──────────────────────────────────────────────────── */}
      <div id="copyPaste">
        <h2>{T.copyPasteTitle} <span>({copiedTexts.length})</span></h2>
        <label>
          <input type="checkbox" id="removeLinks" checked={showLinks} onChange={(e) => setShowLinks(e.target.checked)} />
          &nbsp;{T.showHyperlinks}
        </label>
        <div className="card-container">
          {displayedCopied.map((text, i) => (
            <div key={i} className="card" onClick={() => handleCopiedClick(text)}>
              <p className="bold copy-time">{formatDate(text.time, T.dateLocale)}, {users[text.userId]?.name || T.unknown}</p>
              {text.text}
            </div>
          ))}
        </div>
        {!showAllCopied && copiedTexts.length > 3 && <button onClick={() => setShowAllCopied(true)}>{T.showAll}</button>}
        {showAllCopied && <button onClick={() => setShowAllCopied(false)}>{T.showLess}</button>}
      </div>
      <br />

      {/* ── Graphs ──────────────────────────────────────────────────────── */}
      <div id="writingGraph">
        <div className="graph-container">
          <h2>{T.editsPerDateTitle} <span className="reportUser">{reportUserLabel}</span></h2>
          <p className="text">{T.editsPerDateHint}</p>
          <canvas ref={dateCanvasRef} className="bar-graph" />

          <h2>
            {T.editsByHourTitle} <span className="reportUser">{reportUserLabel}</span> {T.on}{" "}
            <span>{graphDateLabel}</span>
          </h2>
          {selectedGraphDate && <button onClick={() => setSelectedGraphDate(null)}>{T.viewForAllDates}</button>}
          <canvas ref={timeCanvasRef} className="bar-graph" />
        </div>
        <br />
        <h2>{T.timeSpentPerDateTitle} <span className="reportUser">{reportUserLabel}</span></h2>
        <canvas ref={timeDayCanvasRef} className="bar-graph" />
      </div>
      <br />

      {/* ── Writing Sessions (hidden per original) ──────────────────────── */}
      {false && writingSessions.length > 0 && (
        <div>
          <h2>{T.writingSessionsFor} <span className="reportUser">{reportUserLabel}</span> ({writingSessions.length})</h2>
          <p className="text">{T.writingSessionsHint}</p>
          <div>
            {displayedSessions.map((s, i) => (
              <div key={i} className="card">
                <p className="stat"><span className="bold">{T.startTime}</span> {formatDate(s.startTime, T.dateLocale)}</p>
                <p className="stat"><span className="bold">{T.duration}</span> {formatDuration(s.duration, T)}</p>
                <p className="stat"><span className="bold">{T.editsCount}</span> {s.revisions}</p>
              </div>
            ))}
          </div>
          {!showAllSessions && writingSessions.length > 3 && <button onClick={() => setShowAllSessions(true)}>{T.seeAll}</button>}
          {showAllSessions && <button onClick={() => setShowAllSessions(false)}>{T.hide}</button>}
        </div>
      )}

      {/* ── Group Work Stats ─────────────────────────────────────────────── */}
      {multiUser && (
        <div id="group_report_section">
          <h2>{T.groupWorkStats}</h2>
          <select value={groupGraphMode} onChange={(e) => setGroupGraphMode(e.target.value)}>
            <option value="time">{T.timeSpentOption}</option>
            <option value="edits">{T.editsMadeOption}</option>
          </select>
          <canvas ref={groupCanvasRef} style={{ width: "100%", maxWidth: "400px", maxHeight: "400px" }} />
        </div>
      )}
    </div>
  );
};

export default SharedReport;
